import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { gsap, MOTION, useGSAP } from '@/lib/motion'

/**
 * A labelled disc that follows the pointer over anything marked with data-cursor="Label".
 * The system cursor stays visible; this only adds context on precise pointers.
 */
export const CursorLabel: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [label, setLabel] = useState('')

    useGSAP(() => {
        const disc = ref.current

        if (!disc) return

        const mm = gsap.matchMedia()

        mm.add(`${MOTION.ok} and ${MOTION.fine}`, () => {
            gsap.set(disc, { xPercent: -50, yPercent: -50, scale: 0, autoAlpha: 0 })

            const toX = gsap.quickTo(disc, 'x', { duration: 0.45, ease: 'power3.out' })
            const toY = gsap.quickTo(disc, 'y', { duration: 0.45, ease: 'power3.out' })
            let active = false

            const onMove = (event: PointerEvent) => {
                toX(event.clientX)
                toY(event.clientY)

                const host = (event.target as HTMLElement | null)?.closest<HTMLElement>('[data-cursor]')
                const next = host?.dataset.cursor ?? ''

                if (next && !active) {
                    active = true
                    setLabel(next)
                    gsap.to(disc, { scale: 1, autoAlpha: 1, duration: 0.45, ease: 'expo.out', overwrite: 'auto' })
                }
                else if (next && active) {
                    setLabel(next)
                }
                else if (!next && active) {
                    active = false
                    gsap.to(disc, { scale: 0, autoAlpha: 0, duration: 0.35, ease: 'power3.out', overwrite: 'auto' })
                }
            }

            const onLeaveWindow = () => {
                active = false
                gsap.to(disc, { scale: 0, autoAlpha: 0, duration: 0.25, overwrite: 'auto' })
            }

            window.addEventListener('pointermove', onMove, { passive: true })
            document.documentElement.addEventListener('pointerleave', onLeaveWindow)

            return () => {
                window.removeEventListener('pointermove', onMove)
                document.documentElement.removeEventListener('pointerleave', onLeaveWindow)
            }
        })

        return () => mm.revert()
    })

    return (
        <div
            ref={ref}
            aria-hidden='true'
            className='pointer-events-none fixed left-0 top-0 z-[70] hidden size-24 place-items-center rounded-full bg-signal text-ink [@media(hover:hover)_and_(pointer:fine)]:grid'
            style={{ opacity: 0 }}
        >
            <span className='label text-[0.68rem] font-medium'>{label}</span>
        </div>
    )
}
